import {Fragment, useCallback, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import omit from 'lodash/omit';
import uniqBy from 'lodash/uniqBy';
import throttle from 'lodash/throttle';
import { useNavigate } from 'react-router-dom';
import { connect } from 'react-redux';

import Box from '@mui/material/Box';
import Button from "@mui/material/Button";
import Dialog from '@mui/material/Dialog';
import IconCheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import IconCheckBox from '@mui/icons-material/CheckBox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import IconClose from '@mui/icons-material/Close';
import IconExpandLess from '@mui/icons-material/ExpandLess';
import IconExpandMore from '@mui/icons-material/ExpandMore';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import SearchIcon from '@mui/icons-material/Search';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import ViewProvider from '../ViewProvider';
import ProviderListItem from '../ProviderListItem';
import CustomTextField from '../../common/CustomTextField';
import {formatProviderName} from '../../../helpers/providers';
import {useGetProvidersQuery} from '../../../redux/services/providerApi';
import {useGetReviewsQuery} from '../../../redux/services/reviewApi';
import {sessionTypeApi} from '../../../redux/services/sessionTypeApi';
import FormLayout from "../../patientSignup/FormLayout";
import './FindProviderForm.scss';
import useUser from '../../../hooks/useUser';
import * as analytics from '../../../redux/reducers/analytics';
import {getSpecialties} from '../../../helpers/selectOptions';

const defaultFilters = {
  text: '',
  gender: [],
  // experience: [],
  // location: [],
  // availability: [],
  specialty: [],
  treatments: [],
};

const hasFilter = filters => {
  return !Object.keys(filters).every(k => {
    if (k === 'text') {
      return true;
    }
    return !filters[k] || filters[k] === '' || filters[k]?.length <= 0;
  });
}

const filterData = [
  {
    name: 'Provider Gender',
    key: 'gender',
    options: [
      {value: 'male', label: 'Male'},
      {value: 'female', label: 'Female'},
      {value: 'nonbinary', label: 'Non-binary'},
    ]
  },
  // {
  //   name: 'Location',
  //   key: 'location',
  //   options: [
  //     {value: 'home', label: 'My Home'},
  //     {value: 'gym', label: 'My Gym'},
  //     {value: 'workplace', label: 'My Workplace'},
  //     {value: 'virtual', label: 'Virtual Care'},
  //     {value: 'partner', label: 'MX Partner Facility'},
  //   ]
  // },
  {
    name: 'Specialty',
    key: 'specialty',
    options: getSpecialties(),
  },
  {
    name: 'Treatments',
    key: 'treatments',
    options: [
      {value: 'dry_needling', label: 'Dry Needling'},
      {value: 'blood_flow_restriction', label: 'Blood Flow Restriction'},
      {value: 'manual_therapy', label: 'Manual Therapy'},
      {value: 'cupping', label: 'Cupping'},
    ]
  },
];

const formatFilterLabel = ({filterObj, filters}) => {
  if (!filterObj) return '';
  const {name, key, options} = filterObj;
  const filterValues = filters[key];
  const activeOptions = options.filter(o => filterValues.includes(o.value));
  const optionText = activeOptions.reduce((acc, next, i) => (
    i === 0 ? next.label : `${acc} or ${next.label}`
  ), '');
  return `${name} is ${optionText}`;
};

const FilterMenu = props => {
  const {
    filters,
    filterObj,
    onChange
  } = props;
  const {name, key, options} = filterObj;
  const [isOpen, setOpen] = useState(false);
  const activeFilters = filters[key];
  return (
    <MenuItem sx={{minWidth: '300px'}}>
      <List sx={{ width: '100%', maxWidth: 360}}>
        <ListItemButton onClick={() => setOpen(!isOpen)}>
          <ListItemText primary={name} />
          {isOpen ? <IconExpandLess /> : <IconExpandMore />}
        </ListItemButton>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {options.map(({value, label}) =>
              <ListItemButton
                key={value}
                sx={{ pl: 4 }}
                onClick={() => onChange({
                  target: {
                    value: {
                      filterName: value,
                      active: !activeFilters.includes(value)
                    }
                  }
                })}
              >
                <ListItemIcon>
                  {
                    !!filters[key] && activeFilters.includes(value)
                    ? <IconCheckBox color='primary'/>
                    : <IconCheckBoxOutlineBlankIcon color='primary'/>
                  }
                </ListItemIcon>
                <ListItemText
                  primary={label}
                />
              </ListItemButton>
            )}
          </List>
        </Collapse>
      </List>
    </MenuItem>
  );
};

const useTracking = ({props, hasSearched, providers, providerProfile, filters}) => {
  const providersCount = providers?.length || 0;
  const validSearch = hasSearched && (filters?.text?.length || 0) >= 3;
  const safeFilters = {
    text: filters?.text ? 'REDACTED' : null,
    gender: filters.gender,
    specialty: filters?.specialty?.length ? 'REDACTED' : null,
    treatments: filters?.treatments?.length ? 'REDACTED' : null,
  };
  useEffect(() => {
    if (validSearch && providers.length === 0) { // track empty search results
      try {
        console.log('FindProviderForm.noProvidersFound', safeFilters);
        // WARNING: do not log 'text' searches as this could contain PHI
        props.track({eventName: 'FindProviderForm.noProvidersFound', ...safeFilters});
      } catch (e) {
        console.error(e);
      }
    }
    if (validSearch && providers.length > 0) { // track when providers are found
      try {
        console.log('FindProviderForm.providersFound', providersCount, safeFilters);
        // WARNING: do not log 'text' searches as this could contain PHI
        props.track({eventName: 'FindProviderForm.providersFound', providersCount, ...safeFilters});
      } catch (e) {
        console.error(e);
      }
    }
    if (providerProfile) { // track when user views a provider profile
      try {
        console.log('FindProviderForm.viewProviderProfile', providerProfile);
        props.track({eventName: 'FindProviderForm.viewProviderProfile', providerId: providerProfile});
      } catch (e) {
        console.error(e);
      }
    }
  }, [validSearch, providersCount, providerProfile]);
};

const handleInputChange = (
  filters,
  setFilters,
  onSelectProviderId,
  setHasSearched
) => fieldName => e => {
  onSelectProviderId({providerId: null}); // reset provider selection
  const value = get(e, 'target.value');
  if (value) setHasSearched(true);
  const updatedFilters = (
    fieldName === 'text'
    ? {...filters, [fieldName]: value}
    : {
      ...filters,
      [fieldName]: (
        value.active
        ? [...filters[fieldName], value.filterName]
        : filters[fieldName].filter(f => f !== value.filterName)
      )
    }
  );
  return setFilters(updatedFilters);
};

const handleProviderSelect = ({onSelectProviderId, onSubmit}) => providerId => {
  onSelectProviderId({providerId});
  sessionTypeApi.util.resetApiState();
  onSubmit({providerId});
  return;
};

const handleClearFilter = (filters, setFilters) => filterName => {
  const updatedFilters = {
    ...filters,
    [filterName]: [],
  };
  return setFilters(updatedFilters);
};

const FindProviderForm = props => {
  const {
    highlights,
    onSelectProviderId,
    onSubmit,
    setProviderFilters,
  } = props;
  const user = useUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [filters, setFilters] = useState(defaultFilters);
  const [providerProfile, setProviderProfile] = useState(null);
  const [providerSelected, setProviderSelected] = useState(null);
  const [searchIsFocused, setSearchIsFocused] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const onChangeHandler = handleInputChange(
    filters,
    setFilters,
    onSelectProviderId,
    setHasSearched
  );
  const onProviderSelect = handleProviderSelect({onSelectProviderId, onSubmit});
  const onClearFilter = handleClearFilter(filters, setFilters);
  const handleMenuClose = () => setAnchorEl(null);
  const providersQuery = useGetProvidersQuery(
    {...filters, is_hidden: false},
    {skip: false},
  );
  const providers = uniqBy(providersQuery?.data || [], 'id');
  useTracking({
    props, 
    hasSearched, 
    filters,
    providers,
    providerProfile,
  });
  const reviewsQuery = useGetReviewsQuery(
    {provider_id: providerProfile},
    {skip: !providerProfile}
  );
  const reviews = get(reviewsQuery, 'data', []) || [];
  const selectedProviderProfile = (providers || []).find(p => p.id === providerProfile);
  useEffect(() => {
    setProviderFilters(filters);
  }, [filters, setProviderFilters]);
  // debounce text input changes to avoid firing excessive API calls
  const handleTextChange = onChangeHandler('text');
  const throttledTextChangeHandler = useCallback(
    throttle(handleTextChange, 500),
    [handleTextChange]
  );
  return (
    <FormLayout className="FindProviderForm">
      <Typography variant='subtitle1' padding='7.5px 7.5px 3px 7.5px'>
        Choose your Provider
      </Typography>
      <Typography variant='bodyLarge' padding='7.5px 7.5px 3px 7.5px'>
        Who would you like to book a session with?
      </Typography>
      {/* Text Search UI */}
      <CustomTextField
        className='search-input'
        placeholder='Search for a provider by name, city, zip code, or state'
        size='small'
        autoFocus={!!props?.autoFocus}
        onFocus={() => setSearchIsFocused(true)}
        onBlur={() => setSearchIsFocused(false)}
        onChange={(e => throttledTextChangeHandler(e))}
        InputProps={{
          endAdornment: <InputAdornment position="end">
            <SearchIcon className="search-input__icon" />
          </InputAdornment>
        }}
        helperText={filters.text ? 'You can search by first name, last name, city, zip code, or state.' : ''}
      />
      {/* Add chips for each filter that is active*/}
      {
        !hasSearched
        ? <></>
        : <>
          {
            hasFilter(filters) && (
              <Stack
                direction='row'
                spacing={1}
                flexWrap='wrap'
              >
                {Object.keys(filters).map(k =>
                  k === 'text' || !filters[k] || !filters[k].length
                    ? <Fragment key={k}></Fragment>
                    : <Chip
                      key={k}
                      label={formatFilterLabel({
                        filters,
                        filterObj: filterData.find(f => f.key === k),
                      })}
                      variant='outlined'
                      style={{marginTop: '0.5em'}}
                      onDelete={() => onClearFilter(k)}
                    />
                )}
              </Stack>
            )
          }
          {/* Filter menu */}
            <Stack marginTop={'0 !important'}>
              <Stack
                className="filter-menu"
                padding='7.5px 7.5px 1px 0'
                direction='row'
                spacing={2}
                justifyContent='space-between'
                alignItems='center'
                hidden={searchIsFocused}
              >
                <Typography variant='largeBoldLoose' letterSpacing='0.8px'>
                  Select One of {(providers || []).length} Provider{providers.length === 1 ? '' : 's'} Below.
                </Typography>
                <Button
                  className="filter-menu__filter-btn"
                  variant='primaryOutline'
                  size='small'
                  id='fade-button'
                  aria-controls={!!anchorEl ? 'fade-menu' : undefined}
                  aria-haspopup='true'
                  aria-expanded={!!anchorEl ? 'true' : undefined}
                  onClick={e => setAnchorEl(e.currentTarget)}
                  endIcon={<img src="/icons/filter.svg" alt="filter"/>}
                >
                  <Typography variant='small600' color='text.light' lineHeight='1.25rem' textTransform='uppercase'>
                    Filter
                  </Typography>
                </Button>
                <Menu
                  id='fade-menu'
                  anchorEl={anchorEl}
                  open={!!anchorEl}
                  onClose={handleMenuClose}
                  TransitionComponent={Fade}
                  disableScrollLock={true}
                  PaperProps={{
                    style: {
                      maxHeight: 300,
                    },
                  }}
                >
                  {filterData.map(f =>
                    <FilterMenu
                      key={f.key}
                      onChange={onChangeHandler(f.key)}
                      filters={filters}
                      filterObj={f}
                    />
                  )}
                </Menu>
              </Stack>
              {/* Matching Providers */}
              {providers && providers.length
                && (
                  <Stack marginTop={'0 !important'} paddingTop={'15px'} gap={2}>
                    {
                      uniqBy(providers, 'id').map(p =>
                        <>
                          <ProviderListItem
                            key={p.id}
                            provider={p}
                            isSelected={providerSelected?.id === p.id}
                            onSelect={() => {
                              setSearchIsFocused(false);
                              onProviderSelect(p.id);
                            }}
                            onViewProfile={() => {
                              setSearchIsFocused(false);
                              setProviderProfile(p.id)
                            }}
                          />
                        </>
                      )
                    }
                  </Stack>
                )
              }
            </Stack>
            <Dialog
              fullWidth={true}
              maxWidth='lg'
              open={!!selectedProviderProfile}
              onClose={() => setProviderProfile(null)}
            >
              {
                selectedProviderProfile
                  ? <Box>
                    {/* Header */}
                    <Box padding={2} style={{borderBottom: '1px solid rgba(0,0,0,0.15)'}}>
                    <Stack direction='row' justifyContent='space-between'>
                      <Typography variant='h1'>
                        Profile - {formatProviderName(selectedProviderProfile)}
                      </Typography>
                      <IconButton onClick={() => setProviderProfile(null)}>
                        <IconClose />
                      </IconButton>
                    </Stack>
                  </Box>
                  <ViewProvider
                    provider={selectedProviderProfile}
                    highlights={highlights}
                    reviews={reviews}
                    disableRequestSession={true}
                    disableAddReview={true}
                    isPopup={true}
                  />
                </Box>
              : <></>
            }
          </Dialog>
        </>
      }
        <Stack spacing={1}>
          <Divider style={{marginTop: '1em', marginBottom: '1em'}}/>
          <Typography variant='subtitle2' letterSpacing='0.8px'>
            Not sure who to choose?
          </Typography>
          <Stack direction='row' padding={2} justifyContent='center'>
            <Button
              href='https://movementx.typeform.com/provider-match'
              target='_blank'
              size='large'
              variant='outlined'
            >
              Match me with the best provider
            </Button>
          </Stack>
        </Stack>
      </FormLayout>
  );
};

FindProviderForm.defaultProps = {
  providers: PropTypes.arrayOf(PropTypes.object),
};

const mapState = () => ({});

const mapDispatch = dispatch => ({
  track: params => dispatch(analytics.track(params)),
});

export default connect(mapState, mapDispatch)(FindProviderForm);
